
import { ApiCaller } from '@frontend/api'
import type { Content } from '@types'
import { Tag } from '@ui/components'
import { getContentIcon } from '@ui/utils'
import { useSentry } from '@utils/frontend'
import { useState } from 'react'

import SourceModal from './SourceModal'

import { useChatContext } from '../../ChatProvider'

interface SourceProps {
	readonly content: Content;
	readonly type: 'main' | 'secondary';
}

export default function Source({ content, type }: SourceProps) {
	const { sourcesDisplayType, type: chatType } = useChatContext()
	const { captureException } = useSentry()

	const [isModalOpen, setIsModalOpen] = useState(false)
	const [summary, setSummary] = useState<string>('')
	const [title, setTitle] = useState<string>('')

	const icon = getContentIcon(content.type, content.source)

	function handleTagClick() {
		if (sourcesDisplayType === 'extracts') {
			ApiCaller.track('Opened_Source')
			getSummary(content.id)
			setIsModalOpen(true)
		}
	}

	function openDocument() {
		ApiCaller.track('Opened_Source_Chatbot')
		window.open(content.url, '_blank', 'noopener,noreferrer')
	}

	async function getSummary(contentId: string) {
		try {
			const { summary, title } = await ApiCaller.getSummaryAndTitle(contentId)

			if (summary) {
				setSummary(summary)
			}

			if (title) {
				setTitle(title)
			}
		} catch (error) {
			captureException(error, 'error')

			setSummary('')
			setTitle('')
		}
	}

	return (
		<>
			{ type === 'main'
				? (
					<div
						className='relative flex flex-col gap-2 cursor-pointer h-auto py-1 px-2 rounded-md bg-white border border-gray-200 text-gray-400'
						onClick={ chatType === 'chatbot' ? openDocument : handleTagClick }
					>
						<span className='absolute top-[-7px] left-[-6px] rounded-full text-blue-700 bg-blue-100 text-[10px] inline-flex w-4 h-4 justify-center items-center'>
							{ content.index }
						</span>
						<div className='relative text-gray-500 max-h-12 line-clamp-2 text-xs font-semibold break-all'>
							<span className='relative top-[3px] mx-1 inline-flex w-[14px] h-[14px] justify-center items-center text-[14px]'>
								{ icon }
							</span>
							{ content.title }
						</div>
					</div>
				)
				: (
					<Tag
						style='outline'
						disabled={ !content }
						target='_blank'
						to={ sourcesDisplayType === 'link' ? content.url : undefined }
						onClick={ chatType === 'chatbot' ? openDocument : handleTagClick }
					>
						<div className='flex text-xs'>
							<div className='h-4 w-4 mr-2 flex-shrink-0 my-auto text-[16px]'>
								{ icon }
							</div>
							<div className='w-[175px] truncate'>
								{ content.title }
							</div>

						</div>
					</Tag>) }
			<SourceModal
				content={ content }
				isOpen={ isModalOpen }
				summary={ summary }
				title={ title }
				onClose={ () => {
					setIsModalOpen(false)
				} }
			/>
		</>
	)
}
